import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEntitiesByType } from '../hooks/useApi';
import Modal from './Modal';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function EntityList() {
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading, error } = useEntitiesByType(type);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedSubGroups, setExpandedSubGroups] = useState(new Set());
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedSubGroup, setSelectedSubGroup] = useState(null);
  const [showAllGroups, setShowAllGroups] = useState(false);

  const groupedEntities = useMemo(() => {
    if (!data) return null;

    const { entities } = data;

    // Filter and group entities
    const searchLower = searchTerm.toLowerCase();
    const grouped = {};

    entities.forEach(entity => {
      if (entity.entity_text.toLowerCase().includes(searchLower) ||
          entity.group.toLowerCase().includes(searchLower) ||
          (entity.sub_group && entity.sub_group.toLowerCase().includes(searchLower))) {

        const group = entity.group;
        const subGroup = entity.sub_group || 'General';

        if (!grouped[group]) grouped[group] = {};
        if (!grouped[group][subGroup]) grouped[group][subGroup] = [];
        grouped[group][subGroup].push({
          text: entity.readable_form || entity.entity_text,
          group: entity.group,
          subGroup: entity.sub_group,
          count: entity.mention_count,
          authors: entity.authors
        });
      }
    });

    // Sort everything
    Object.keys(grouped).forEach(group => {
      Object.keys(grouped[group]).forEach(subGroup => {
        grouped[group][subGroup].sort((a, b) => {
          // If counts are different, sort by count
          if (b.count !== a.count) {
            return b.count - a.count;
          }
          // If counts are equal, sort alphabetically
          return a.text.localeCompare(b.text);
        });
      });
    });

    // Calculate total mentions per group and subgroup for sorting
    const groupTotals = {};
    Object.entries(grouped).forEach(([group, subGroups]) => {
      groupTotals[group] = {
        total: 0,
        subGroups: {}
      };

      Object.entries(subGroups).forEach(([subGroup, entities]) => {
        const subGroupTotal = entities.reduce((sum, entity) => sum + entity.count, 0);
        groupTotals[group].subGroups[subGroup] = subGroupTotal;
        groupTotals[group].total += subGroupTotal;
      });
    });

    // Sort groups and subgroups by mentions
    const sortedGrouped = {};
    Object.entries(grouped)
      .sort(([groupA, _], [groupB, __]) => {
        return groupTotals[groupB].total - groupTotals[groupA].total;
      })
      .forEach(([group, subGroups]) => {
        sortedGrouped[group] = {};
        Object.entries(subGroups)
          .sort(([subGroupA, _], [subGroupB, __]) => {
            return groupTotals[group].subGroups[subGroupB] - groupTotals[group].subGroups[subGroupA];
          })
          .forEach(([subGroup, entities]) => {
            sortedGrouped[group][subGroup] = entities;
          });
      });
    return sortedGrouped;
  }, [data, searchTerm]);

  // Filter the grouped entities based on selection
  const filteredGroupedEntities = useMemo(() => {
    if (!groupedEntities) return null;
    if (!selectedGroup) return groupedEntities;

    const filtered = { [selectedGroup]: {} };
    if (!selectedSubGroup) {
      filtered[selectedGroup] = groupedEntities[selectedGroup];
    } else {
      filtered[selectedGroup] = {
        [selectedSubGroup]: groupedEntities[selectedGroup][selectedSubGroup]
      };
    }
    return filtered;
  }, [groupedEntities, selectedGroup, selectedSubGroup]);

  // Get visible groups based on selection or all if none selected
  const visibleGroups = useMemo(() => {
    if (!filteredGroupedEntities) return [];
    if (selectedGroup) return [selectedGroup];
    return Object.keys(filteredGroupedEntities);
  }, [selectedGroup, filteredGroupedEntities]);

  const toggleGroupExpansion = (group) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleSubGroupExpansion = (groupAndSubGroup) => {
    const newExpanded = new Set(expandedSubGroups);
    if (newExpanded.has(groupAndSubGroup)) {
      newExpanded.delete(groupAndSubGroup);
    } else {
      newExpanded.add(groupAndSubGroup);
    }
    setExpandedSubGroups(newExpanded);
  };

  const chartData = useMemo(() => {
    if (!groupedEntities) return null;

    const groups = Object.entries(groupedEntities).map(([group, subGroups]) => ({
      group,
      total: Object.values(subGroups).flat().reduce((sum, entity) => sum + entity.count, 0)
    }));

    // Sort by total mentions
    groups.sort((a, b) => b.total - a.total);

    // Take top 10 groups
    const top10Groups = groups.slice(0, 10);

    return {
      labels: top10Groups.map(g => g.group),
      datasets: [
        {
          label: 'Total Mentions',
          data: top10Groups.map(g => g.total),
          backgroundColor: 'rgba(59, 130, 246, 0.5)', // blue-500 with opacity
          borderColor: 'rgb(59, 130, 246)', // blue-500
          borderWidth: 1,
        },
      ],
    };
  }, [groupedEntities]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: true,
        text: 'Top 10 Most Mentioned Entity Groups',
        font: {
          size: 14,
          weight: '500'
        },
        padding: {
          top: 4,
          bottom: 10
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Mentions',
          font: {
            size: 11
          }
        },
        ticks: {
          font: {
            size: 10
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 10
          },
          maxRotation: 45,
          minRotation: 45
        }
      }
    },
    onClick: (_, elements) => {
      if (elements.length > 0) {
        const clickedGroup = chartData.labels[elements[0].index];
        setSelectedGroup(selectedGroup === clickedGroup ? null : clickedGroup);
        setSelectedSubGroup(null); // Reset subgroup selection when changing groups
      }
    }
  };

  const getSubGroupChartData = (group, subGroups) => {
    const subGroupData = Object.entries(subGroups).map(([subGroup, entities]) => ({
      subGroup,
      total: entities.reduce((sum, entity) => sum + entity.count, 0)
    }));

    // Sort by total mentions
    subGroupData.sort((a, b) => b.total - a.total);

    // Limit to 6 items if not expanded
    const limitedData = expandedGroups.has(group) ? subGroupData : subGroupData.slice(0, 6);

    return {
      labels: limitedData.map(sg => sg.subGroup),
      datasets: [
        {
          label: 'Mentions by Sub-group',
          data: limitedData.map(sg => sg.total),
          backgroundColor: 'rgba(99, 102, 241, 0.5)', // indigo-500 with opacity
          borderColor: 'rgb(99, 102, 241)', // indigo-500
          borderWidth: 1,
        },
      ],
    };
  };

  const subGroupChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        titleFont: {
          size: 11
        },
        bodyFont: {
          size: 11
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Mentions',
          font: {
            size: 11
          }
        },
        ticks: {
          font: {
            size: 10
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 10
          },
          maxRotation: 45,
          minRotation: 45
        }
      }
    },
    onClick: (_, elements) => {
      if (elements.length > 0) {
        const clickedSubGroup = getSubGroupChartData(selectedGroup, groupedEntities[selectedGroup]).labels[elements[0].index];
        setSelectedSubGroup(selectedSubGroup === clickedSubGroup ? null : clickedSubGroup);
      }
    }
  };

  // Add effect to handle anchor scrolling and group expansion
  useEffect(() => {
    if (location.hash) {
      // Remove the # from the hash
      const targetId = decodeURIComponent(location.hash.slice(1));

      // Find if this is a group or subgroup
      const matchingGroup = Object.keys(groupedEntities || {}).find(group => group === targetId);

      if (matchingGroup) {
        // If it's a group, expand it and scroll to it
        setExpandedGroups(prev => new Set(prev).add(matchingGroup));
        setShowAllGroups(true); // Show all groups if the target is not in first 5

        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const element = document.getElementById(`group-${targetId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Check if it's a subgroup
        Object.entries(groupedEntities || {}).forEach(([group, subGroups]) => {
          Object.keys(subGroups).forEach(subGroup => {
            if (subGroup === targetId) {
              setExpandedGroups(prev => new Set(prev).add(group));
              setShowAllGroups(true);
              setExpandedSubGroups(prev => new Set(prev).add(`${group}-${subGroup}`));

              setTimeout(() => {
                const element = document.getElementById(`subgroup-${group}-${targetId}`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }
          });
        });
      }
    }
  }, [location.hash, groupedEntities]);

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">Error loading entities: {error.message}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">{type}</h2>
        <div className="flex gap-4">
          {(selectedGroup || selectedSubGroup) && (
            <button
              onClick={() => {
                setSelectedGroup(null);
                setSelectedSubGroup(null);
              }}
              className="text-blue-600 hover:text-blue-800 px-4 py-2 rounded-lg border border-blue-600"
            >
              Clear Filters
            </button>
          )}
          <input
            type="text"
            placeholder="Search entities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2"
          />
        </div>
      </div>

      {chartData && (
        <div className="bg-white rounded-lg shadow p-3 sm:p-6 mb-4 sm:mb-6">
          <div className="h-[250px] sm:h-[300px]">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {Object.entries(filteredGroupedEntities || {})
        .slice(0, showAllGroups ? undefined : 5)
        .map(([group, subGroups]) => {
          const subGroupCount = Object.keys(subGroups).length;
          const isExpanded = expandedGroups.has(group);
          const hasMoreThanSix = subGroupCount > 6;
          const shouldShowChart = subGroupCount >= 5;

          return (
            <div
              key={group}
              className="space-y-4"
              id={`group-${group}`}
            >
              <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700">
                  {group}
                </h3>
                {hasMoreThanSix && (
                  <button
                    onClick={() => toggleGroupExpansion(group)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                  >
                    {isExpanded ? 'Show Less' : `Show All (${subGroupCount})`}
                    <svg
                      className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}
              </div>

              {shouldShowChart && (
                <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-4">
                  <div className="h-[200px] sm:h-[250px]">
                    <Bar
                      data={getSubGroupChartData(group, subGroups)}
                      options={subGroupChartOptions}
                    />
                  </div>
                </div>
              )}

              {Object.entries(subGroups)
                .sort(([_, entitiesA], [__, entitiesB]) => entitiesB.length - entitiesA.length)
                .slice(0, isExpanded ? undefined : 6)
                .map(([subGroup, entities]) => {
                  const groupSubGroupKey = `${group}-${subGroup}`;
                  const isSubGroupExpanded = expandedSubGroups.has(groupSubGroupKey);
                  const displayedEntities = isSubGroupExpanded ? entities : entities.slice(0, 6);
                  const hasMoreEntities = entities.length > 6;

                  return (
                    <div
                      key={`${group}-${subGroup}`}
                      className="space-y-4"
                      id={`subgroup-${group}-${subGroup}`}
                    >
                      <div className="flex justify-between items-center">
                        {subGroup !== 'General' && (
                          <button
                            onClick={() => navigate(`/topic/${encodeURIComponent(`${group} - ${subGroup}`)}`)}
                            className="text-lg font-medium text-blue-600 hover:text-blue-800"
                          >
                            {subGroup}
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {displayedEntities.map((entity, index) => (
                          <div
                            key={`${group}-${subGroup}-${entity.text}-${entity.count}-${index}`}
                            className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => navigate(`/type/${encodeURIComponent(type)}/entity/${encodeURIComponent(entity.text)}`)}
                                className="text-blue-600 hover:text-blue-800 text-lg font-medium"
                              >
                                {entity.text}
                              </button>
                              <span className="text-gray-500 text-sm">
                                {entity.count} case stud{entity.count !== 1 ? 'ies' : 'y'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {hasMoreEntities && (
                        <button
                          onClick={() => toggleSubGroupExpansion(groupSubGroupKey)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                        >
                          {isSubGroupExpanded ? 'Show Less' : `Show More (${entities.length - 6} more)`}
                          <svg
                            className={`w-4 h-4 transform transition-transform ${isSubGroupExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}

      {Object.keys(filteredGroupedEntities || {}).length > 5 && !showAllGroups && (
        <button
          onClick={() => setShowAllGroups(true)}
          className="w-full py-3 text-blue-600 hover:text-blue-800 font-medium border border-blue-600 rounded-lg mt-4 flex items-center justify-center gap-2"
        >
          Show More Groups
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {showAllGroups && (
        <button
          onClick={() => setShowAllGroups(false)}
          className="w-full py-3 text-blue-600 hover:text-blue-800 font-medium border border-blue-600 rounded-lg mt-4 flex items-center justify-center gap-2"
        >
          Show Less
          <svg
            className="w-4 h-4 transform rotate-180"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      <Modal
        isOpen={!!selectedEntity}
        onClose={() => setSelectedEntity(null)}
        title="Entity Details"
      >
        {selectedEntity && (
          <div>
            <h3 className="font-semibold text-lg">{selectedEntity.text}</h3>
            <p className="text-gray-600">Type: {type}</p>
            {selectedEntity.group !== 'Uncategorized' && (
              <p className="text-gray-600">Group: {selectedEntity.group}</p>
            )}
            {selectedEntity.subGroup && (
              <p className="text-gray-600">Sub-group: {selectedEntity.subGroup}</p>
            )}
            <div className="mt-4">
              <h4 className="font-medium">Mentioned in:</h4>
              <ul className="list-disc pl-5 space-y-2">
                {[...selectedEntity.authors].map(author => (
                  <li key={author}>{author}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default EntityList;